import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import { Upload, Loader2, FileText, Sparkles, Trash2, Download, FileCheck2 } from "lucide-react";

export default function CandidateProfile() {
  const { t } = useI18n();
  const { authHeader, user } = useAuth();
  const [profile, setProfile] = useState({ name: "", phone: "", title: "", location: "", languages: "", experience_summary: "", work_history: "", education: "", skills: "" });
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState("");

  const load = async () => {
    const r = await axios.get(`${API}/candidate/profile`, { headers: authHeader });
    const p = r.data.profile || {};
    setProfile({
      name: p.name || "",
      phone: p.phone || "",
      title: p.title || "",
      location: p.location || "",
      languages: p.languages || "",
      experience_summary: p.experience_summary || "",
      work_history: p.work_history || "",
      education: p.education || "",
      skills: p.skills || "",
    });
    setCv(r.data.cv || null);
    if (p.generated_cv_text) setGeneratedText(p.generated_cv_text);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/candidate/profile`, profile, { headers: authHeader });
      toast.success("Profile saved");
    } catch (e) { toast.error("Save failed"); }
    setLoading(false);
  };

  const uploadCv = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/candidate/cv`, fd, { headers: { ...authHeader, "Content-Type": "multipart/form-data" } });
      setCv({ id: r.data.id, original_filename: r.data.filename });
      toast.success("CV uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const removeCv = async () => {
    await axios.delete(`${API}/candidate/cv`, { headers: authHeader });
    setCv(null);
    toast.success("CV removed");
  };

  const downloadCv = async () => {
    if (!cv) return;
    try {
      const res = await axios.get(`${API}/files/${cv.id}/download`, { headers: authHeader, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = cv.original_filename || "cv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const generateCv = async () => {
    setGenerating(true);
    try {
      // First save current profile so AI has latest data
      await axios.patch(`${API}/candidate/profile`, profile, { headers: authHeader });
      const r = await axios.post(`${API}/candidate/cv/generate`, {}, { headers: authHeader });
      setGeneratedText(r.data.text);
      // Refresh CV info
      load();
      toast.success("CV generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    setGenerating(false);
  };

  return (
    <div className="space-y-6" data-testid="candidate-profile">
      <Card className="p-6">
        <h3 className="font-display text-xl font-semibold text-slate-900">{t("candidate.cvOnFile")}</h3>
        <div className="mt-4 flex flex-col sm:flex-row gap-3 items-start">
          {cv ? (
            <div className="flex-1 flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <FileCheck2 className="h-6 w-6 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{cv.original_filename}</div>
                <div className="text-xs text-slate-500">{cv.content_type || "—"} · {Math.round((cv.size || 0) / 1024)} KB</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={downloadCv} data-testid="cv-download" className="rounded-full"><Download className="h-3 w-3 mr-1" />{t("candidate.downloadCv")}</Button>
                <Button size="sm" variant="ghost" onClick={removeCv} data-testid="cv-remove" className="text-rose-600 hover:bg-rose-50"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4 text-sm">{t("candidate.noCv")}</div>
          )}
          <label className="inline-flex items-center gap-2 bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full px-5 h-10 cursor-pointer text-sm font-medium">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {cv ? t("candidate.replaceCv") : t("candidate.uploadCv")}
            <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" data-testid="cv-upload" onChange={(e) => e.target.files?.[0] && uploadCv(e.target.files[0])} />
          </label>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-display text-xl font-semibold text-slate-900">{t("candidate.profileTitle")}</h3>
        <p className="text-sm text-slate-500">{t("candidate.profileSubtitle")}</p>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>{t("apply.name")}</Label><Input data-testid="prof-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
          <div><Label>{t("apply.phone")}</Label><Input data-testid="prof-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
          <div><Label>{t("candidate.headline")}</Label><Input data-testid="prof-title" placeholder="e.g. Senior accountant" value={profile.title} onChange={(e) => setProfile({ ...profile, title: e.target.value })} /></div>
          <div><Label>{t("candidate.location")}</Label><Input data-testid="prof-location" placeholder="e.g. Port-Louis" value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} /></div>
        </div>
        <div><Label>{t("candidate.languages")}</Label><Input data-testid="prof-languages" placeholder="English, French, Creole" value={profile.languages} onChange={(e) => setProfile({ ...profile, languages: e.target.value })} /></div>
        <div><Label>{t("apply.experience")}</Label><Textarea data-testid="prof-experience" rows={3} placeholder={t("apply.experiencePh")} value={profile.experience_summary} onChange={(e) => setProfile({ ...profile, experience_summary: e.target.value })} /></div>
        <div><Label>{t("apply.workHistory")}</Label><Textarea data-testid="prof-history" rows={4} placeholder={t("apply.workHistoryPh")} value={profile.work_history} onChange={(e) => setProfile({ ...profile, work_history: e.target.value })} /></div>
        <div><Label>{t("apply.education")}</Label><Textarea data-testid="prof-education" rows={2} placeholder={t("apply.educationPh")} value={profile.education} onChange={(e) => setProfile({ ...profile, education: e.target.value })} /></div>
        <div><Label>{t("apply.skills")}</Label><Input data-testid="prof-skills" placeholder={t("apply.skillsPh")} value={profile.skills} onChange={(e) => setProfile({ ...profile, skills: e.target.value })} /></div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={save} disabled={loading} data-testid="prof-save" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("candidate.saveProfile")}
          </Button>
          <Button onClick={generateCv} disabled={generating} variant="outline" data-testid="prof-generate-cv" className="rounded-full border-[#1A8FE3] text-[#1A8FE3] hover:bg-sky-50">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}{t("candidate.generateCv")}
          </Button>
        </div>
      </Card>

      {generatedText && (
        <Card className="p-6" data-testid="generated-cv-card">
          <h3 className="font-display text-xl font-semibold text-slate-900 inline-flex items-center gap-2"><FileText className="h-5 w-5 text-[#1A8FE3]" />{t("candidate.generated")}</h3>
          <pre className="mt-4 whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-5 leading-relaxed font-sans">{generatedText}</pre>
        </Card>
      )}
    </div>
  );
}
